<script>
	import { onMount, onDestroy } from "svelte";
	import { spring } from "svelte/motion";

	// --- Configuration ---
	const agentConfig = {
		architect: {
			role: "Architect",
			icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z", // User
			color: "text-purple-400",
			glow: "shadow-[0_0_20px_rgba(168,85,247,0.4)]",
		},
		eng: {
			role: "Engineering",
			icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4", // Building
			color: "text-blue-400",
			glow: "shadow-[0_0_20px_rgba(96,165,250,0.4)]",
			isBuilding: true,
		},
		secops: {
			role: "SecOps",
			icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z", // Lock
			color: "text-red-400",
			glow: "shadow-[0_0_20px_rgba(248,113,113,0.4)]",
		},
		qa: {
			role: "QA Bot",
			icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z", // Check circle
			color: "text-green-400",
			glow: "shadow-[0_0_20px_rgba(74,222,128,0.4)]",
		},
		analyst: {
			role: "Analyst",
			icon: "M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z", // Graph
			color: "text-yellow-400",
			glow: "shadow-[0_0_20px_rgba(250,204,21,0.4)]",
		},
	};

	// --- Architect Logic (Spring Follow) ---
	const architectPos = spring(
		{ x: 50, y: 50 },
		{ stiffness: 0.03, damping: 0.15 }, // Delayed, draggy feel
	);

	let isFollowing = false;
	let followTimer = null;

	function handleMouseMove(e) {
		// Only start following loop if not already "active"
		if (!isFollowing) {
			if (followTimer) clearTimeout(followTimer);
			followTimer = setTimeout(() => {
				isFollowing = true;
			}, 1000); // 1s delay (adjust as needed contextually)
		}

		if (isFollowing) {
			const rect = e.currentTarget.getBoundingClientRect();
			architectPos.set({
				x: ((e.clientX - rect.left) / rect.width) * 100,
				y: ((e.clientY - rect.top) / rect.height) * 100,
			});
		}
	}

	function handleMouseLeave() {
		isFollowing = false;
		if (followTimer) clearTimeout(followTimer);
		architectPos.set({ x: 50, y: 50 }); // Return to center/home
	}

	// --- Ping Pong Agents (Autonomous) ---
	let pingPongAgents = [
		{ id: "eng", x: 20, y: 30, vx: 0.03, vy: 0.02 },
		{ id: "secops", x: 80, y: 20, vx: -0.04, vy: 0.03 },
		{ id: "qa", x: 25, y: 80, vx: 0.02, vy: -0.05 },
		{ id: "analyst", x: 75, y: 75, vx: -0.03, vy: -0.02 },
	];

	let rafId;

	function updateLoop() {
		pingPongAgents = pingPongAgents.map((ag) => {
			let { x, y, vx, vy } = ag;
			x += vx;
			y += vy;

			// Bounce off walls (10% - 90%)
			if (x <= 10 || x >= 90) vx *= -1;
			if (y <= 10 || y >= 90) vy *= -1;

			// Hard clamp
			x = Math.max(10, Math.min(90, x));
			y = Math.max(10, Math.min(90, y));

			return { ...ag, x, y, vx, vy };
		});

		rafId = requestAnimationFrame(updateLoop);
	}

	onMount(() => {
		updateLoop();
	});

	onDestroy(() => {
		if (rafId) cancelAnimationFrame(rafId);
		if (followTimer) clearTimeout(followTimer);
	});
</script>

<section
	class="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#0a0a0f] text-white pt-20 group"
	on:mousemove={handleMouseMove}
	on:mouseleave={handleMouseLeave}
	role="region"
	aria-label="Interactive Hero Animation"
>
	<!-- Interactive Agent Layer -->
	<div class="absolute inset-0 pointer-events-none overflow-hidden">
		<!-- Central Ambient Glow -->
		<div
			class="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.15)_0%,transparent_70%)]"
		></div>

		<!-- 1. Render Ping Pong Agents (Autonomous) -->
		{#each pingPongAgents as agent (agent.id)}
			{@const cfg = agentConfig[agent.id]}
			<div
				class="absolute flex flex-col items-center gap-3 transition-transform will-change-transform"
				style="left: {agent.x}%; top: {agent.y}%; transform: translate(-50%, -50%);"
			>
				<!-- Icon -->
				<div
					class="relative rounded-2xl bg-[#13131f]/90 border border-white/10 backdrop-blur-md {cfg.glow} p-4"
				>
					<svg
						class="{cfg.isBuilding
							? 'w-10 h-10'
							: 'w-8 h-8'} {cfg.color} drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="1.5"
							d={cfg.icon}
						/>
					</svg>
					{#if cfg.isBuilding}
						<div
							class="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-ping"
						></div>
					{/if}
				</div>
				<!-- Label -->
				<div
					class="flex items-center gap-2 px-3 py-1 rounded-full bg-black/60 border border-white/10 backdrop-blur-md"
				>
					<span
						class="text-[9px] uppercase tracking-widest text-gray-400 font-bold font-mono"
						>{cfg.role}</span
					>
				</div>
			</div>
		{/each}

		<!-- 2. Render Architect (Leader - Follows Mouse) -->
		<div
			class="absolute flex flex-col items-center gap-3 transition-transform will-change-transform z-10"
			style="left: {$architectPos.x}%; top: {$architectPos.y}%; transform: translate(-50%, -50%);"
		>
			<div
				class="relative rounded-2xl bg-[#13131f]/90 border border-white/10 backdrop-blur-md {agentConfig
					.architect.glow} p-5 scale-110"
			>
				<svg
					class="w-10 h-10 {agentConfig.architect
						.color} drop-shadow-[0_0_15px_rgba(168,85,247,0.6)]"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="1.5"
						d={agentConfig.architect.icon}
					/>
				</svg>
				<div
					class="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0a0a0f] shadow-[0_0_10px_rgba(34,197,94,0.8)] animate-pulse"
				></div>
			</div>
			<div
				class="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 border border-white/10 backdrop-blur-md shadow-xl border-purple-500/30"
			>
				<span
					class="text-[10px] uppercase tracking-widest text-white font-bold font-mono"
					>ARCHITECT</span
				>
				<span
					class="w-1.5 h-1.5 bg-purple-500 rounded-full shadow-glow animate-pulse"
				></span>
			</div>
		</div>

		<!-- Connecting Lines (Optional visual flair: lines between Architect and others when hovering? maybe too complex for now, keep it clean) -->

		<!-- Grid Pattern Overlay -->
		<div
			class="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_50%,transparent_90%)] -z-10"
		></div>

		<!-- Gradients -->
		<div
			class="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"
		></div>
		<div
			class="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"
		></div>
	</div>

	<div class="relative container mx-auto px-6 pt-12 pb-24 z-10">
		<div class="flex flex-col items-center text-center mb-20">
			<!-- Badge -->
			<div
				class="animate-fade-in opacity-0"
				style="animation-delay: 0.1s;"
			>
				<span
					class="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-primary-300 backdrop-blur-sm shadow-[0_0_15px_rgba(124,58,237,0.2)]"
				>
					<span
						class="w-1.5 h-1.5 rounded-full bg-neon-cyan animate-pulse"
					></span>
					AI-Operated Infrastructure
				</span>
			</div>

			<!-- Main Heading -->
			<h1
				class="mt-8 mb-6 text-5xl md:text-7xl font-bold leading-tight tracking-tight animate-slide-up opacity-0"
				style="animation-delay: 0.2s;"
			>
				Build Your OS
				<span
					class="block bg-gradient-to-r from-white via-primary-300 to-gray-400 bg-clip-text text-transparent"
				>
					At The Speed of AI
				</span>
			</h1>

			<!-- Subheading -->
			<p
				class="mb-10 max-w-2xl text-lg md:text-xl leading-relaxed text-gray-400 animate-slide-up opacity-0"
				style="animation-delay: 0.3s;"
			>
				Bring your ideas to life with our AI builder team. Transform
				abstract requirements into production-ready operating platforms.
				<br />
				<span class="text-white">Auto-scaling. Self-healing.</span>
			</p>

			<!-- Buttons -->
			<div
				class="flex flex-col sm:flex-row gap-5 animate-slide-up opacity-0"
				style="animation-delay: 0.4s;"
			>
				<a
					href="/start"
					class="group relative px-8 py-4 bg-primary text-white text-lg font-bold rounded-xl overflow-hidden transition-all duration-300 hover:bg-primary-dark hover:scale-105 hover:shadow-[0_0_40px_rgba(124,58,237,0.5)] shadow-[0_0_20px_rgba(124,58,237,0.3)]"
				>
					<div
						class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"
					></div>
					<span class="relative z-10">Let's Start</span>
				</a>
				<a
					href="/how-it-works"
					class="px-8 py-4 rounded-xl border border-white/10 bg-white/5 text-lg font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/10 hover:border-white/20 hover:text-white"
				>
					View Architecture
				</a>
			</div>
		</div>

		<!-- 3D Dashboard Mockup with Perspective Tilt -->
		<div
			class="relative w-full max-w-5xl mx-auto perspective-[2000px] group animate-slide-up opacity-0"
			style="animation-delay: 0.6s;"
		>
			<!-- Glow behind -->
			<div
				class="absolute -inset-1 bg-gradient-to-r from-primary via-neon-cyan to-primary rounded-[2rem] opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-1000"
			></div>

			<div
				class="relative rounded-[20px] bg-[#0f1016] border border-white/10 p-2 shadow-2xl transition-transform duration-700 ease-out preserve-3d"
				style="transform: rotateX({scrollY * 0.02}deg) rotateY(0deg);"
			>
				<!-- Window Controls -->
				<div class="absolute top-4 left-6 flex gap-2 z-20">
					<div class="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
					<div class="w-3 h-3 rounded-full bg-[#febc2e]"></div>
					<div class="w-3 h-3 rounded-full bg-[#28c840]"></div>
				</div>

				<!-- Glass Grid Overlay -->
				<div
					class="bg-[#13131f] rounded-2xl overflow-hidden min-h-[400px] md:min-h-[500px] grid grid-cols-12 grid-rows-6 gap-1 p-1"
				>
					<!-- Sidebar -->
					<div
						class="col-span-2 row-span-6 bg-[#1c1c2e]/50 border-r border-white/5 p-4 hidden md:flex flex-col gap-4"
					>
						<div class="h-8 w-8 rounded bg-white/10 mb-4"></div>
						<div class="h-2 w-16 rounded bg-white/10"></div>
						<div class="h-2 w-12 rounded bg-white/5"></div>
						<div class="h-2 w-20 rounded bg-white/5"></div>
						<div
							class="mt-auto h-8 w-8 rounded-full bg-gradient-to-br from-primary to-blue-500"
						></div>
					</div>

					<!-- Header Area -->
					<div
						class="col-span-12 md:col-span-10 row-span-1 border-b border-white/5 flex items-center justify-between px-6 pt-8 pb-2"
					>
						<div class="flex gap-4">
							<div
								class="h-6 w-24 rounded bg-white/10 animate-pulse"
							></div>
							<div class="h-6 w-16 rounded bg-white/5"></div>
						</div>
						<div
							class="h-8 w-32 rounded bg-primary/20 border border-primary/30 flex items-center justify-center text-xs text-primary font-mono"
						>
							● SYSTEM ONLINE
						</div>
					</div>

					<!-- Main Content Grid -->
					<div
						class="col-span-12 md:col-span-10 row-span-5 p-6 grid grid-cols-3 gap-6"
					>
						<!-- Stats Card 1 -->
						<div
							class="col-span-3 md:col-span-1 rounded-xl bg-white/5 border border-white/5 p-5 relative overflow-hidden group/card hover:border-primary/50 transition-colors"
						>
							<div
								class="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity"
							></div>
							<div
								class="h-8 w-8 rounded-lg bg-primary/20 mb-4 flex items-center justify-center text-primary"
							>
								<svg
									class="w-5 h-5"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									><path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M13 10V3L4 14h7v7l9-11h-7z"
									/></svg
								>
							</div>
							<div class="text-3xl font-bold text-white mb-1">
								99.99%
							</div>
							<div
								class="text-xs text-gray-400 uppercase tracking-wider"
							>
								Uptime Guaranteed
							</div>
							<!-- Graph line -->
							<div
								class="mt-4 h-1 w-full bg-white/10 rounded-full overflow-hidden"
							>
								<div class="h-full bg-primary w-[99%]"></div>
							</div>
						</div>

						<!-- Stats Card 2 -->
						<div
							class="col-span-3 md:col-span-1 rounded-xl bg-white/5 border border-white/5 p-5 relative overflow-hidden group/card hover:border-neon-cyan/50 transition-colors"
						>
							<div
								class="absolute inset-0 bg-gradient-to-br from-neon-cyan/10 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity"
							></div>
							<div
								class="h-8 w-8 rounded-lg bg-neon-cyan/20 mb-4 flex items-center justify-center text-neon-cyan"
							>
								<svg
									class="w-5 h-5"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									><path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
									/></svg
								>
							</div>
							<div class="text-3xl font-bold text-white mb-1">
								45ms
							</div>
							<div
								class="text-xs text-gray-400 uppercase tracking-wider"
							>
								Latency <span class="text-neon-cyan"
									>(Global)</span
								>
							</div>
							<div class="mt-4 flex gap-1">
								<div
									class="h-3 w-1 bg-neon-cyan/20 rounded-sm"
								></div>
								<div
									class="h-3 w-1 bg-neon-cyan/40 rounded-sm"
								></div>
								<div
									class="h-3 w-1 bg-neon-cyan/60 rounded-sm"
								></div>
								<div
									class="h-3 w-1 bg-neon-cyan rounded-sm"
								></div>
								<div
									class="h-3 w-1 bg-neon-cyan/60 rounded-sm"
								></div>
							</div>
						</div>

						<!-- Stats Card 3 -->
						<div
							class="col-span-3 md:col-span-1 rounded-xl bg-white/5 border border-white/5 p-5 relative overflow-hidden group/card hover:border-neon-purple/50 transition-colors"
						>
							<div
								class="absolute inset-0 bg-gradient-to-br from-neon-purple/10 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity"
							></div>
							<div
								class="h-8 w-8 rounded-lg bg-neon-purple/20 mb-4 flex items-center justify-center text-neon-purple"
							>
								<svg
									class="w-5 h-5"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									><path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
									/></svg
								>
							</div>
							<div class="text-3xl font-bold text-white mb-1">
								SOC2
							</div>
							<div
								class="text-xs text-gray-400 uppercase tracking-wider"
							>
								Compliance Ready
							</div>
							<div class="mt-4 flex items-center gap-2">
								<div
									class="h-2 w-2 rounded-full bg-green-400 animate-pulse"
								></div>
								<span class="text-[10px] text-gray-400"
									>Audited Today</span
								>
							</div>
						</div>

						<!-- Big Chart Area -->
						<div
							class="col-span-3 md:col-span-3 row-span-2 rounded-xl bg-black/20 border border-white/5 relative overflow-hidden"
						>
							<!-- Fake Chart SVG -->
							<svg
								class="absolute inset-0 w-full h-full p-4"
								viewBox="0 0 400 100"
								preserveAspectRatio="none"
							>
								<path
									d="M0,80 Q50,70 100,40 T200,60 T300,20 T400,50"
									fill="none"
									stroke="url(#gradient)"
									stroke-width="3"
								/>
								<defs>
									<linearGradient
										id="gradient"
										x1="0%"
										y1="0%"
										x2="100%"
										y2="0%"
									>
										<stop
											offset="0%"
											stop-color="#7c3aed"
										/>
										<stop
											offset="100%"
											stop-color="#00f2ea"
										/>
									</linearGradient>
								</defs>
								<path
									d="M0,80 Q50,70 100,40 T200,60 T300,20 T400,50 V100 H0 Z"
									fill="url(#gradient-fill)"
									opacity="0.1"
								/>
								<defs>
									<linearGradient
										id="gradient-fill"
										x1="0%"
										y1="0%"
										x2="0%"
										y2="100%"
									>
										<stop
											offset="0%"
											stop-color="#7c3aed"
										/>
										<stop
											offset="100%"
											stop-color="transparent"
										/>
									</linearGradient>
								</defs>
							</svg>

							<!-- Floating Activity Bubbles -->
							<div
								class="absolute top-1/4 left-1/4 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-[10px] border border-white/5 animate-float"
							>
								Deployed v2.4
							</div>
							<div
								class="absolute bottom-1/3 right-1/4 px-3 py-1 rounded-full bg-primary/20 backdrop-blur-md text-[10px] border border-primary/20 text-primary-200 animate-float"
								style="animation-delay: 1s;"
							>
								scaled to 500 instances
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</section>
